import { GLUCOSE_CONTEXT_TEXT, type Reading } from './build';

export type ReadingKind = Reading['kind'];

/** The readings people record, in the order screens list them. */
export const READING_KINDS: readonly ReadingKind[] = ['blood_pressure', 'blood_glucose', 'weight', 'pulse', 'spo2', 'temperature'];

export const READING_LABELS: Readonly<Record<ReadingKind, string>> = {
  blood_pressure: 'Blood pressure',
  blood_glucose: 'Blood sugar',
  weight: 'Weight',
  pulse: 'Pulse',
  spo2: 'SpO2',
  temperature: 'Temperature',
};

/** Units as screens show them. Temperature is kept in °C and shown in both scales. */
export const READING_UNITS: Readonly<Record<ReadingKind, string>> = {
  blood_pressure: 'mmHg',
  blood_glucose: 'mg/dL',
  weight: 'kg',
  pulse: 'beats a minute',
  spo2: '%',
  temperature: '°C',
};

/**
 * The range a value must fall in to be saved. They are wide enough for any
 * real reading and catch typing slips, such as a weight entered in grams.
 */
export const READING_LIMITS = {
  systolic: { min: 50, max: 300 },
  diastolic: { min: 30, max: 200 },
  bpm: { min: 20, max: 250 },
  percent: { min: 50, max: 100 },
  celsius: { min: 30, max: 45 },
  kg: { min: 0.5, max: 350 },
  mgPerDl: { min: 20, max: 800 },
} as const;

export function fahrenheitFromCelsius(celsius: number): number {
  return Math.round((celsius * 9) / 5 * 10 + 320) / 10;
}

export function celsiusFromFahrenheit(fahrenheit: number): number {
  return Math.round(((fahrenheit - 32) * 5) / 9 * 10) / 10;
}

function outside(value: number, limit: { min: number; max: number }): boolean {
  return !Number.isFinite(value) || value < limit.min || value > limit.max;
}

/** Why a reading cannot be saved, in words fit to show, or null when it is fine. */
export function readingProblem(reading: Reading): string | null {
  if (Number.isNaN(Date.parse(reading.at))) return 'Enter when the reading was taken.';
  if (Date.parse(reading.at) > Date.now() + 5 * 60_000) return 'The reading’s time is in the future.';
  switch (reading.kind) {
    case 'blood_pressure':
      if (outside(reading.systolic, READING_LIMITS.systolic)) return 'Enter the upper (systolic) number, between 50 and 300.';
      if (outside(reading.diastolic, READING_LIMITS.diastolic)) return 'Enter the lower (diastolic) number, between 30 and 200.';
      if (reading.diastolic >= reading.systolic) return 'The lower number must be less than the upper number.';
      return null;
    case 'pulse':
      return outside(reading.bpm, READING_LIMITS.bpm) ? 'Enter a pulse between 20 and 250 beats a minute.' : null;
    case 'spo2':
      return outside(reading.percent, READING_LIMITS.percent) ? 'Enter an SpO2 between 50 and 100%.' : null;
    case 'temperature':
      return outside(reading.celsius, READING_LIMITS.celsius) ? 'Enter a temperature between 30 and 45 °C (86 and 113 °F).' : null;
    case 'weight':
      return outside(reading.kg, READING_LIMITS.kg) ? 'Enter a weight between 0.5 and 350 kg.' : null;
    case 'blood_glucose':
      return outside(reading.mgPerDl, READING_LIMITS.mgPerDl) ? 'Enter a blood sugar between 20 and 800 mg/dL.' : null;
  }
}

const GLUCOSE_WORDS = { fasting: 'fasting', after_meal: 'after a meal', random: 'random' } as const satisfies Record<keyof typeof GLUCOSE_CONTEXT_TEXT, string>;

/** One line describing a reading, such as "Blood pressure 128/84 mmHg". */
export function readingText(reading: Reading): string {
  switch (reading.kind) {
    case 'blood_pressure':
      return `Blood pressure ${reading.systolic}/${reading.diastolic} mmHg`;
    case 'pulse':
      return `Pulse ${reading.bpm} beats a minute`;
    case 'spo2':
      return `SpO2 ${reading.percent}%`;
    case 'temperature':
      return `Temperature ${reading.celsius.toFixed(1)} °C (${fahrenheitFromCelsius(reading.celsius).toFixed(1)} °F)`;
    case 'weight':
      return `Weight ${reading.kg} kg`;
    case 'blood_glucose':
      return `Blood sugar ${reading.mgPerDl} mg/dL${reading.context ? `, ${GLUCOSE_WORDS[reading.context]}` : ''}`;
  }
}

/** The numbers a chart plots for a reading: two for blood pressure, one for the rest. */
export function readingValues(reading: Reading): number[] {
  switch (reading.kind) {
    case 'blood_pressure':
      return [reading.systolic, reading.diastolic];
    case 'pulse':
      return [reading.bpm];
    case 'spo2':
      return [reading.percent];
    case 'temperature':
      return [reading.celsius];
    case 'weight':
      return [reading.kg];
    case 'blood_glucose':
      return [reading.mgPerDl];
  }
}
